import React, { useState, useEffect, useCallback } from 'react';
import { PiggyBank, TrendingDown, Calendar, IndianRupee, Calculator } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

// Prepayment Calculator Constants
const MIN_LOAN_AMOUNT = 500000; // ₹5 Lakhs
const MAX_LOAN_AMOUNT = 50000000; // ₹5 Crore
const LOAN_AMOUNT_STEP = 100000;
const MIN_INTEREST_RATE = 6;
const MAX_INTEREST_RATE = 20;
const INTEREST_RATE_STEP = 0.1;
const MIN_TENURE_MONTHS = 12;
const MAX_TENURE_MONTHS = 360;
const TENURE_STEP_MONTHS = 12;

export const PrepaymentCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000000); // ₹50 Lakhs
  const [interestRate, setInterestRate] = useState(9.5);
  const [tenure, setTenure] = useState(240); // 20 years
  const [prepaymentAmount, setPrepaymentAmount] = useState(500000); // ₹5 Lakhs
  const [prepaymentMonth, setPrepaymentMonth] = useState(12); // After 1 year
  
  const [withoutPrepayment, setWithoutPrepayment] = useState({
    emi: 0,
    totalInterest: 0,
    totalAmount: 0
  });
  
  const [withPrepayment, setWithPrepayment] = useState({
    newTenure: 0,
    totalInterest: 0,
    totalAmount: 0,
    interestSaved: 0,
    tenureReduced: 0
  });

  const calculateEMI = (principal, rate, tenureMonths) => {
    const ratePerMonth = rate / 12 / 100;
    if (ratePerMonth === 0) {
      return principal / tenureMonths;
    }
    return (
      (principal * ratePerMonth * Math.pow(1 + ratePerMonth, tenureMonths)) /
      (Math.pow(1 + ratePerMonth, tenureMonths) - 1)
    );
  };

  const calculatePrepaymentImpact = useCallback(() => {
    const ratePerMonth = interestRate / 12 / 100;
    
    // Calculate without prepayment
    const emi = calculateEMI(loanAmount, interestRate, tenure);
    const totalAmountWithoutPrepay = emi * tenure;
    const totalInterestWithoutPrepay = totalAmountWithoutPrepay - loanAmount;
    
    setWithoutPrepayment({
      emi: emi,
      totalInterest: totalInterestWithoutPrepay,
      totalAmount: totalAmountWithoutPrepay
    });

    // Calculate with prepayment
    // Calculate outstanding principal at prepayment month
    let outstandingPrincipal = loanAmount;
    for (let month = 1; month <= prepaymentMonth; month++) {
      const interestForMonth = outstandingPrincipal * ratePerMonth;
      const principalPaid = emi - interestForMonth;
      outstandingPrincipal -= principalPaid;
    }

    // Apply prepayment
    const newPrincipal = outstandingPrincipal - prepaymentAmount;
    
    if (newPrincipal <= 0) {
      setWithPrepayment({
        newTenure: prepaymentMonth,
        totalInterest: 0,
        totalAmount: loanAmount,
        interestSaved: totalInterestWithoutPrepay,
        tenureReduced: tenure - prepaymentMonth
      });
      return;
    }

    // Calculate new tenure with same EMI
    let remainingTenure = 0;
    let balance = newPrincipal;
    let totalInterestPaid = 0;

    // Interest paid till prepayment
    for (let month = 1; month <= prepaymentMonth; month++) {
      const interestForMonth = (month === 1 ? loanAmount : balance) * ratePerMonth;
      totalInterestPaid += interestForMonth;
      const principalPaid = emi - interestForMonth;
      balance -= principalPaid;
    }

    // Calculate remaining tenure
    balance = newPrincipal;
    while (balance > 0 && remainingTenure < 360) {
      const interestForMonth = balance * ratePerMonth;
      const principalPaid = emi - interestForMonth;
      
      if (principalPaid <= 0) break;
      
      totalInterestPaid += interestForMonth;
      balance -= principalPaid;
      remainingTenure++;
    }

    const newTotalTenure = prepaymentMonth + remainingTenure;
    const totalAmountWithPrepay = (emi * newTotalTenure) + prepaymentAmount;
    const interestSaved = totalInterestWithoutPrepay - totalInterestPaid;
    const tenureReduced = tenure - newTotalTenure;

    setWithPrepayment({
      newTenure: newTotalTenure,
      totalInterest: totalInterestPaid,
      totalAmount: totalAmountWithPrepay,
      interestSaved: interestSaved,
      tenureReduced: tenureReduced
    });
  }, [loanAmount, interestRate, tenure, prepaymentAmount, prepaymentMonth]);

  useEffect(() => {
    calculatePrepaymentImpact();
  }, [calculatePrepaymentImpact]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatMonths = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${remainingMonths} months`;
    if (remainingMonths === 0) return `${years} years`;
    return `${years} years ${remainingMonths} months`;
  };

  return (
    <section id="prepayment-calculator" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Prepayment Impact <span className="text-emerald-600">Calculator</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            See how a lump sum prepayment can reduce your interest burden and loan tenure
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Input Section */}
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Loan Details
              </CardTitle>
              <CardDescription>Enter your loan information and prepayment details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Loan Amount */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Loan Amount</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(loanAmount)}</span>
                </div>
                <Slider
                  value={[loanAmount]}
                  onValueChange={(value) => setLoanAmount(value[0])}
                  min={MIN_LOAN_AMOUNT}
                  max={MAX_LOAN_AMOUNT}
                  step={LOAN_AMOUNT_STEP}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>₹5L</span>
                  <span>₹5Cr</span>
                </div>
              </div>

              {/* Interest Rate */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Interest Rate (per annum)</Label>
                  <span className="text-xl font-bold text-emerald-600">{interestRate.toFixed(1)}%</span>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(value) => setInterestRate(value[0])}
                  min={MIN_INTEREST_RATE}
                  max={MAX_INTEREST_RATE}
                  step={INTEREST_RATE_STEP}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>6%</span>
                  <span>20%</span>
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Loan Tenure</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatMonths(tenure)}</span>
                </div>
                <Slider
                  value={[tenure]}
                  onValueChange={(value) => setTenure(value[0])}
                  min={MIN_TENURE_MONTHS}
                  max={MAX_TENURE_MONTHS}
                  step={TENURE_STEP_MONTHS}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>1 year</span>
                  <span>30 years</span>
                </div>
              </div>

              {/* Prepayment Amount */}
              <div className="space-y-4 pt-6 border-t-2 border-emerald-100">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Prepayment Amount</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(prepaymentAmount)}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={prepaymentAmount === 500000 ? "default" : "outline"}
                    onClick={() => setPrepaymentAmount(500000)}
                    className={prepaymentAmount === 500000 ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  >
                    ₹5 Lakhs
                  </Button>
                  <Button
                    variant={prepaymentAmount === 1000000 ? "default" : "outline"}
                    onClick={() => setPrepaymentAmount(1000000)}
                    className={prepaymentAmount === 1000000 ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  >
                    ₹10 Lakhs
                  </Button>
                  <Button
                    variant={prepaymentAmount === 2000000 ? "default" : "outline"}
                    onClick={() => setPrepaymentAmount(2000000)}
                    className={prepaymentAmount === 2000000 ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  >
                    ₹20 Lakhs
                  </Button>
                </div>
                <Slider
                  value={[prepaymentAmount]}
                  onValueChange={(value) => setPrepaymentAmount(value[0])}
                  min={100000}
                  max={Math.min(loanAmount * 0.5, 5000000)}
                  step={100000}
                  className="w-full"
                />
              </div>

              {/* Prepayment After */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Prepayment After</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatMonths(prepaymentMonth)}</span>
                </div>
                <Slider
                  value={[prepaymentMonth]}
                  onValueChange={(value) => setPrepaymentMonth(value[0])}
                  min={6}
                  max={Math.min(120, tenure - 12)}
                  step={6}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Without Prepayment */}
            <Card className="border-2 border-slate-200 bg-slate-50">
              <CardHeader>
                <CardTitle className="text-slate-700">Without Prepayment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Monthly EMI</span>
                  <span className="text-xl font-bold text-slate-900">{formatCurrency(withoutPrepayment.emi)}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Total Interest</span>
                  <span className="text-xl font-bold text-red-600">{formatCurrency(withoutPrepayment.totalInterest)}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg">
                  <span className="text-slate-600">Total Amount Payable</span>
                  <span className="text-xl font-bold text-slate-900">{formatCurrency(withoutPrepayment.totalAmount)}</span>
                </div>
              </CardContent>
            </Card>

            {/* With Prepayment */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <PiggyBank className="w-6 h-6" />
                  With Prepayment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-emerald-200">
                  <span className="text-slate-600">New Loan Tenure</span>
                  <span className="text-xl font-bold text-emerald-600">{formatMonths(withPrepayment.newTenure)}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-emerald-200">
                  <span className="text-slate-600">Total Interest</span>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(withPrepayment.totalInterest)}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-white rounded-lg border border-emerald-200">
                  <span className="text-slate-600">Total Amount Payable</span>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(withPrepayment.totalAmount)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Savings Highlights */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
                <CardContent className="pt-6">
                  <TrendingDown className="w-8 h-8 mb-2" />
                  <p className="text-sm opacity-90 mb-1">Interest Saved</p>
                  <p className="text-2xl font-bold">{formatCurrency(withPrepayment.interestSaved)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="pt-6">
                  <Calendar className="w-8 h-8 mb-2" />
                  <p className="text-sm opacity-90 mb-1">Tenure Reduced</p>
                  <p className="text-2xl font-bold">{formatMonths(withPrepayment.tenureReduced)}</p>
                </CardContent>
              </Card>
            </div>

            {/* CTA */}
            <Card className="bg-emerald-600 text-white border-0">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-bold mb-2">Ready to save lakhs in interest?</h3>
                <p className="mb-4 opacity-90">Contact us to discuss your prepayment options</p>
                <Button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="bg-white text-emerald-600 hover:bg-slate-100"
                >
                  Get Started Today
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
